import { useState, useEffect } from 'react';
import { useSalaries } from '../../hooks/useData';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Trash2, Users, Edit, Crown, Briefcase, Calculator, ArrowRight, TrendingUp, UserPlus, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { BulletinsPaieSection, CongesSection } from '../../components/shared/PaieSection';
import SaisieMensuelle from '../../components/shared/SaisieMensuelle';

const PatronSalaires = () => {
  const { salaries, loading, refetch } = useSalaries();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  const [newSalary, setNewSalary] = useState({ nom: '', salaire_net: 0, taux_charges: 42, type_contrat: 'salarie' });
  const [simNet, setSimNet] = useState(2000);
  const [simTaux, setSimTaux] = useState(45);
  const [simTab, setSimTab] = useState('tns');
  const [simMode, setSimMode] = useState('net');
  const [simBudget, setSimBudget] = useState(4000);
  const [extras, setExtras] = useState([]);
  const [showAddExtra, setShowAddExtra] = useState(false);
  const [newExtra, setNewExtra] = useState({ nom: '', date: new Date().toISOString().split('T')[0], montant: 0, motif: 'remplacement', type_paiement: 'direct', tva: false, detail: '' });
  const [extraMonth, setExtraMonth] = useState(new Date().toISOString().slice(0, 7));

  const fetchExtras = async () => {
    try {
      const res = await api.get(`/extras?mois=${extraMonth}`);
      setExtras(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchExtras(); }, [extraMonth]);

  const handleAddExtra = async () => {
    try {
      await api.post('/extras', newExtra);
      toast.success('Extra ajouté');
      setShowAddExtra(false);
      setNewExtra({ nom: '', date: new Date().toISOString().split('T')[0], montant: 0, motif: 'remplacement', type_paiement: 'direct', tva: false, detail: '' });
      fetchExtras();
    } catch { toast.error("Erreur"); }
  };

  const handleDeleteExtra = async (id) => {
    try {
      await api.delete(`/extras/${id}`);
      toast.success('Supprimé');
      fetchExtras();
    } catch { toast.error('Erreur'); }
  };

  const TAUX_CHARGES_EXTRA = 42;
  const totalExtrasNet = extras.filter(e => e.type_paiement !== 'prestataire').reduce((sum, e) => sum + (e.montant || 0), 0);
  const totalExtrasCharges = Math.round(totalExtrasNet * TAUX_CHARGES_EXTRA / 100);
  const totalExtrasPrestaHT = extras.filter(e => e.type_paiement === 'prestataire').reduce((sum, e) => {
    if (e.tva) return sum + Math.round(e.montant / 1.2);
    return sum + (e.montant || 0);
  }, 0);
  const totalExtrasTVA = extras.filter(e => e.type_paiement === 'prestataire' && e.tva).reduce((sum, e) => sum + Math.round(e.montant - e.montant / 1.2), 0);
  const totalExtrasPresta = extras.filter(e => e.type_paiement === 'prestataire').reduce((sum, e) => sum + (e.montant || 0), 0);
  const totalExtras = totalExtrasNet + totalExtrasCharges + totalExtrasPresta;

  const dirigeant = salaries.find(s => s.type_contrat === 'dirigeant_tns');
  const employes = salaries.filter(s => s.type_contrat !== 'dirigeant_tns');

  useEffect(() => {
    if (dirigeant) {
      setSimNet(dirigeant.salaire_net);
      setSimTaux(dirigeant.taux_charges);
    }
  }, [dirigeant?.salaire_net, dirigeant?.taux_charges]);

  const handleAdd = async () => {
    try {
      await api.post('/salaries', newSalary);
      toast.success('Ajouté');
      setShowAddDialog(false);
      setNewSalary({ nom: '', salaire_net: 0, taux_charges: 42, type_contrat: 'salarie' });
      refetch();
    } catch { toast.error("Erreur lors de l'ajout"); }
  };

  const handleUpdate = async (salary) => {
    try {
      await api.put(`/salaries/${salary.id}`, salary);
      toast.success('Mis à jour');
      setEditingSalary(null);
      refetch();
    } catch { toast.error('Erreur lors de la mise à jour'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/salaries/${id}`);
      toast.success('Supprimé');
      refetch();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const calcCharges = (s) => (s.salaire_net || 0) * ((s.taux_charges || 42) / 100);
  const calcTotal = (s) => (s.salaire_net || 0) + calcCharges(s);

  const totalNetEmployes = employes.reduce((sum, s) => sum + (s.salaire_net || 0), 0);
  const totalChargesEmployes = employes.reduce((sum, s) => sum + calcCharges(s), 0);
  const totalCoutEmployes = totalNetEmployes + totalChargesEmployes;

  const dirNet = dirigeant ? dirigeant.salaire_net : 0;
  const dirCharges = dirigeant ? calcCharges(dirigeant) : 0;
  const dirTotal = dirNet + dirCharges;

  const grandTotalNet = dirNet + totalNetEmployes;
  const grandTotalCharges = dirCharges + totalChargesEmployes;
  const grandTotal = grandTotalNet + grandTotalCharges + totalExtras;

  const SalaryRow = ({ s, showType }) => (
    <div className="flex items-center justify-between py-3 border-b border-[rgb(var(--lm-border)/0.5)] last:border-0" data-testid={`salary-row-${s.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {showType && (
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${s.type_contrat === 'dirigeant_tns' ? 'bg-lemas-gold/20' : 'bg-[rgb(var(--lm-surface-elevated))]'}`}>
            {s.type_contrat === 'dirigeant_tns' ? <Crown size={12} className="text-[rgb(var(--lm-gold))]" /> : <Briefcase size={12} className="text-[rgb(var(--lm-text-secondary))]" />}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm text-[rgb(var(--lm-text))] font-medium truncate">{s.nom}</p>
          <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
            {s.type_contrat === 'dirigeant_tns' ? 'Travailleur Indépendant' : 'Salarié'} · {s.taux_charges}% cotisations
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className="text-sm font-mono text-[rgb(var(--lm-text))]">{formatCurrency(s.salaire_net)}</p>
          <p className="text-xs font-mono text-[rgb(var(--lm-text-secondary))]">net</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-mono text-orange-400">{formatCurrency(calcCharges(s))}</p>
          <p className="text-xs font-mono text-[rgb(var(--lm-text-secondary))]">cotisations</p>
        </div>
        <div className="text-right min-w-[80px]">
          <p className="text-sm font-mono text-[rgb(var(--lm-gold))] font-semibold">{formatCurrency(calcTotal(s))}</p>
          <p className="text-xs font-mono text-[rgb(var(--lm-text-secondary))]">coût total</p>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditingSalary({...s})} className="text-[rgb(var(--lm-text-secondary))] hover:text-[rgb(var(--lm-text))] h-8 w-8 p-0">
            <Edit size={14} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-400/60 hover:text-red-400 h-8 w-8 p-0">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="patron-salaires">
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Masse salariale</p>
          <h1 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Users size={20} style={{ color: 'rgb(var(--lm-gold))' }} />
            Salaires & Cotisations
          </h1>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))] hover:opacity-90" data-testid="add-salary-btn">
              <Plus size={18} className="mr-2" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))]">
            <DialogHeader>
              <DialogTitle className="font-title text-xl">Ajouter une personne</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Nom</label>
                <Input value={newSalary.nom} onChange={(e) => setNewSalary({...newSalary, nom: e.target.value})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Prénom Nom" data-testid="new-salary-name" />
              </div>
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Rémunération nette mensuelle</label>
                <Input type="number" value={newSalary.salaire_net || ''} onChange={(e) => setNewSalary({...newSalary, salaire_net: parseFloat(e.target.value) || 0})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" data-testid="new-salary-net" />
              </div>
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Type</label>
                <select value={newSalary.type_contrat} onChange={(e) => {
                  const type = e.target.value;
                  setNewSalary({...newSalary, type_contrat: type, taux_charges: type === 'dirigeant_tns' ? 45 : 42 });
                }} className="w-full bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] rounded-md px-3 py-2 text-sm" data-testid="new-salary-type">
                  <option value="salarie">Salarié</option>
                  <option value="dirigeant_tns">Dirigeant / Travailleur Indépendant (TNS)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Taux cotisations %</label>
                <Input type="number" value={newSalary.taux_charges} onChange={(e) => setNewSalary({...newSalary, taux_charges: parseFloat(e.target.value) || 0})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" data-testid="new-salary-taux" />
              </div>
              <Button onClick={handleAdd} className="w-full bg-lemas-gold text-lemas-bg" data-testid="confirm-add-btn">Ajouter</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dirigeant TNS */}
      {dirigeant && (
        <Card className="bg-[rgb(var(--lm-surface))] border-lemas-gold/30 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }} data-testid="dirigeant-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[rgb(var(--lm-gold))] flex items-center gap-2">
              <Crown size={16} /> Dirigeant — Travailleur Indépendant
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SalaryRow s={dirigeant} showType={false} />
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Rémunération nette</p>
                <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{formatCurrency(dirNet)}</p>
              </div>
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Cotisations TNS ({dirigeant.taux_charges}%)</p>
                <p className="font-mono text-lg text-orange-400">{formatCurrency(dirCharges)}</p>
              </div>
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center border border-lemas-gold/20">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Coût total dirigeant</p>
                <p className="font-mono text-lg text-[rgb(var(--lm-gold))]">{formatCurrency(dirTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Salariés */}
      <Card className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }} data-testid="employes-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Briefcase size={16} className="text-[rgb(var(--lm-text-secondary))]" /> Salariés ({employes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {employes.length === 0 ? (
            <p className="text-sm text-[rgb(var(--lm-text-secondary))] text-center py-4">Aucun salarié</p>
          ) : (
            employes.map(s => <SalaryRow key={s.id} s={s} showType={false} />)
          )}
          {employes.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Total net salariés</p>
                <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{formatCurrency(totalNetEmployes)}</p>
              </div>
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Total cotisations</p>
                <p className="font-mono text-lg text-orange-400">{formatCurrency(totalChargesEmployes)}</p>
              </div>
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 text-center">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Coût total salariés</p>
                <p className="font-mono text-lg text-[rgb(var(--lm-gold))]">{formatCurrency(totalCoutEmployes)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extras */}
      <Card className="bg-[rgb(var(--lm-surface))] border-purple-400/20 animate-slide-up" style={{ animationDelay: '180ms', animationFillMode: 'backwards' }} data-testid="extras-section">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <CardTitle className="text-base text-[rgb(var(--lm-text))] flex items-center gap-2">
              <UserPlus size={16} className="text-purple-400" /> Extras ({extras.length})
              <span className="text-xs text-[rgb(var(--lm-text-muted))] font-normal ml-1 hidden sm:inline">— Remplacements & Prestations traiteur</span>
            </CardTitle>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <input type="month" value={extraMonth} onChange={e => setExtraMonth(e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-2 py-1 text-xs text-[rgb(var(--lm-text))] flex-1 sm:flex-none" data-testid="extra-month-filter" />
              <Button size="sm" onClick={() => setShowAddExtra(true)} className="bg-purple-500 hover:bg-purple-600 text-[rgb(var(--lm-text))]" data-testid="add-extra-btn">
                <Plus size={14} className="mr-1" /> Extra
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {extras.length === 0 ? (
            <p className="text-sm text-[rgb(var(--lm-text-secondary))] text-center py-4">Aucun extra ce mois</p>
          ) : (
            <div className="space-y-2">
              {extras.map(e => {
                const isDirect = e.type_paiement !== 'prestataire';
                const charges = isDirect ? Math.round(e.montant * TAUX_CHARGES_EXTRA / 100) : 0;
                const coutTotal = e.montant + charges;
                return (
                <div key={e.id} className="flex items-center justify-between py-2.5 px-3 bg-[rgb(var(--lm-bg))]/40 rounded-lg" data-testid={`extra-row-${e.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${e.motif === 'traiteur' ? 'bg-blue-400' : 'bg-orange-400'}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-[rgb(var(--lm-text))] font-medium">{e.nom}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDirect ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))]' : 'bg-cyan-400/10 text-cyan-400'}`}>
                          {isDirect ? 'Direct' : 'Facture'}
                        </span>
                      </div>
                      <p className="text-xs text-[rgb(var(--lm-text-muted))] truncate">
                        {e.date} · <span className={e.motif === 'traiteur' ? 'text-blue-400' : 'text-orange-400'}>
                          {e.motif === 'traiteur' ? 'Traiteur' : 'Remplacement'}
                        </span>
                        {e.detail && <span> · {e.detail}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-2">
                    {isDirect ? (
                      <div className="text-right">
                        <span className="font-mono text-sm text-purple-400">{formatCurrency(coutTotal)}</span>
                        <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(e.montant)} net + {formatCurrency(charges)} charges</p>
                      </div>
                    ) : (
                      <div className="text-right">
                        <span className="font-mono text-sm text-cyan-400">{formatCurrency(e.montant)}</span>
                        {e.tva ? (
                          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(Math.round(e.montant / 1.2))} HT + {formatCurrency(Math.round(e.montant - e.montant / 1.2))} TVA</p>
                        ) : (
                          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Auto-entrepreneur · sans TVA</p>
                        )}
                      </div>
                    )}
                    <button onClick={() => handleDeleteExtra(e.id)} className="text-red-400/50 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
          {extras.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-purple-400/5 border border-purple-400/20 rounded-lg p-3 text-center">
                <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] uppercase">Coût Total</p>
                <p className="font-mono text-lg text-purple-400">{formatCurrency(totalExtras)}</p>
              </div>
              <div className="bg-white/5 border border-[rgb(var(--lm-border))] rounded-lg p-3 text-center">
                <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] uppercase">Direct + Charges</p>
                <p className="font-mono text-sm text-[rgb(var(--lm-text))]">{formatCurrency(totalExtrasNet + totalExtrasCharges)}</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{formatCurrency(totalExtrasNet)} + {TAUX_CHARGES_EXTRA}%</p>
              </div>
              <div className="bg-cyan-400/5 border border-cyan-400/20 rounded-lg p-3 text-center">
                <p className="text-[10px] text-cyan-400 uppercase">Prestataires</p>
                <p className="font-mono text-sm text-cyan-400">{formatCurrency(totalExtrasPresta)}</p>
                {totalExtrasTVA > 0 && <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">dont {formatCurrency(totalExtrasTVA)} TVA récup.</p>}
              </div>
              <div className="bg-lemas-gold/5 border border-lemas-gold/20 rounded-lg p-3 text-center">
                <p className="text-[10px] text-[rgb(var(--lm-gold))] uppercase">Coût réel HT</p>
                <p className="font-mono text-sm text-[rgb(var(--lm-gold))]">{formatCurrency(totalExtras - totalExtrasTVA)}</p>
                {totalExtrasTVA > 0 && <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">TVA déductible</p>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Extra Dialog */}
      {showAddExtra && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowAddExtra(false)}>
          <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="add-extra-modal">
            <h3 className="font-title text-lg mb-4 flex items-center gap-2"><UserPlus size={16} className="text-purple-400" /> Ajouter un extra</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Nom</label>
                <Input value={newExtra.nom} onChange={e => setNewExtra({...newExtra, nom: e.target.value})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Prénom de l'extra" data-testid="extra-nom" />
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Date</label>
                <Input type="date" value={newExtra.date} onChange={e => setNewExtra({...newExtra, date: e.target.value})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="extra-date" />
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Montant payé</label>
                <Input type="number" step="0.01" value={newExtra.montant || ''} onChange={e => setNewExtra({...newExtra, montant: parseFloat(e.target.value) || 0})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0.00" data-testid="extra-montant" />
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Motif</label>
                <select value={newExtra.motif} onChange={e => setNewExtra({...newExtra, motif: e.target.value})}
                  className="w-full bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] rounded-md px-3 py-2 text-sm" data-testid="extra-motif">
                  <option value="remplacement">Remplacement congé</option>
                  <option value="traiteur">Prestation traiteur</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Type de paiement</label>
                <select value={newExtra.type_paiement || 'direct'} onChange={e => setNewExtra({...newExtra, type_paiement: e.target.value})}
                  className="w-full bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] rounded-md px-3 py-2 text-sm" data-testid="extra-type-paiement">
                  <option value="direct">Extra direct (payé par moi)</option>
                  <option value="prestataire">Prestataire (envoie une facture)</option>
                </select>
              </div>
              {newExtra.type_paiement === 'prestataire' && (
                <div className="flex items-center gap-3 bg-[rgb(var(--lm-surface-elevated))] rounded-lg px-3 py-2.5">
                  <label className="flex items-center gap-2 cursor-pointer flex-1" data-testid="extra-tva-toggle">
                    <input type="checkbox" checked={newExtra.tva} onChange={e => setNewExtra({...newExtra, tva: e.target.checked})}
                      className="w-4 h-4 rounded border-white/20 bg-[rgb(var(--lm-bg))] accent-lemas-gold" />
                    <span className="text-sm text-[rgb(var(--lm-text))]">Assujetti TVA (20%)</span>
                  </label>
                  <span className="text-xs text-[rgb(var(--lm-text-muted))]">{newExtra.tva ? 'Avec TVA' : 'Auto-entrepreneur / Sans TVA'}</span>
                </div>
              )}
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Détail (optionnel)</label>
                <Input value={newExtra.detail} onChange={e => setNewExtra({...newExtra, detail: e.target.value})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Ex: service du soir, mariage..." data-testid="extra-detail" />
              </div>
              <Button onClick={handleAddExtra} className="w-full bg-purple-500 text-[rgb(var(--lm-text))] hover:bg-purple-600" data-testid="confirm-add-extra">Ajouter</Button>
            </div>
          </div>
        </div>
      )}

      {/* Grand Total */}
      <Card className="bg-[rgb(var(--lm-surface))] border-lemas-gold/20 animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards' }} data-testid="total-section">
        <CardContent className="pt-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-[10px] md:text-xs text-[rgb(var(--lm-text-secondary))] uppercase tracking-wider">Rémunérations</p>
              <p className="font-mono text-xl md:text-2xl text-[rgb(var(--lm-text))] mt-1">{formatCurrency(grandTotalNet)}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">dirigeant + salariés</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] md:text-xs text-[rgb(var(--lm-text-secondary))] uppercase tracking-wider">Cotisations</p>
              <p className="font-mono text-xl md:text-2xl text-orange-400 mt-1">{formatCurrency(grandTotalCharges)}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">TNS + patronales</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] md:text-xs text-purple-400 uppercase tracking-wider">Extras</p>
              <p className="font-mono text-xl md:text-2xl text-purple-400 mt-1">{formatCurrency(totalExtras)}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">{extras.length} extra{extras.length > 1 ? 's' : ''}</p>
            </div>
            <div className="text-center md:border-l md:border-lemas-gold/20 md:pl-4 col-span-2 md:col-span-1 bg-lemas-gold/5 md:bg-transparent rounded-lg py-3 md:py-0">
              <p className="text-[10px] md:text-xs text-[rgb(var(--lm-gold))] uppercase tracking-wider font-semibold">Coût Total Mensuel</p>
              <p className="font-mono text-2xl md:text-2xl text-[rgb(var(--lm-gold))] mt-1">{formatCurrency(grandTotal)}</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">annuel: {formatCurrency(grandTotal * 12)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Saisie Paie Mensuelle */}
      <SaisieMensuelle salaries={salaries} />

      {/* Bulletins Gmail */}
      <BulletinsPaieSection onImported={refetch} />
      <CongesSection />

      {/* Simulateur de coût */}
      <Card className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }} data-testid="simulateur-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-[rgb(var(--lm-gold))] flex items-center gap-2">
            <Calculator size={16} /> Simulateur de coût
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Tabs */}
          <div className="flex gap-2 mb-5">
            <button onClick={() => { setSimTab('tns'); setSimTaux(45); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${simTab === 'tns' ? 'bg-lemas-gold/20 text-[rgb(var(--lm-gold))]' : 'bg-white/5 text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))]'}`} data-testid="sim-tab-tns">
              <Crown size={12} className="inline mr-1" /> Dirigeant TNS
            </button>
            <button onClick={() => { setSimTab('salarie'); setSimTaux(42); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${simTab === 'salarie' ? 'bg-blue-400/20 text-blue-400' : 'bg-white/5 text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))]'}`} data-testid="sim-tab-salarie">
              <Briefcase size={12} className="inline mr-1" /> Salarié
            </button>
          </div>

          {/* Mode toggle */}
          <div className="flex gap-2 mb-5">
            <button onClick={() => setSimMode('net')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors ${simMode === 'net' ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'}`} data-testid="sim-mode-net">
              <ArrowRight size={12} /> Net → Coût total
            </button>
            <button onClick={() => setSimMode('budget')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors ${simMode === 'budget' ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'}`} data-testid="sim-mode-budget">
              <ArrowRight size={12} className="rotate-180" /> Budget → Net possible
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input side */}
            <div className="space-y-4">
              {simMode === 'net' ? (
                <div>
                  <label className="text-sm text-[rgb(var(--lm-text-secondary))] mb-2 block">
                    {simTab === 'tns' ? 'Rémunération nette souhaitée' : 'Salaire net souhaité'}
                  </label>
                  <div className="flex items-center gap-3">
                    <Input type="number" value={simNet} onChange={(e) => setSimNet(parseFloat(e.target.value) || 0)} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-lg" data-testid="sim-net-input" />
                    <span className="text-[rgb(var(--lm-text-secondary))] text-sm whitespace-nowrap">€ / mois</span>
                  </div>
                  <input type="range" min={0} max={8000} step={100} value={simNet} onChange={(e) => setSimNet(parseFloat(e.target.value))} className="w-full mt-2 accent-lemas-gold" data-testid="sim-slider" />
                  <div className="flex justify-between text-xs text-[rgb(var(--lm-text-muted))] mt-1"><span>0€</span><span>4 000€</span><span>8 000€</span></div>
                </div>
              ) : (
                <div>
                  <label className="text-sm text-[rgb(var(--lm-text-secondary))] mb-2 block">Budget coût total mensuel</label>
                  <div className="flex items-center gap-3">
                    <Input type="number" value={simBudget} onChange={(e) => setSimBudget(parseFloat(e.target.value) || 0)} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono text-lg" data-testid="sim-budget-input" />
                    <span className="text-[rgb(var(--lm-text-secondary))] text-sm whitespace-nowrap">€ / mois</span>
                  </div>
                  <input type="range" min={0} max={12000} step={100} value={simBudget} onChange={(e) => setSimBudget(parseFloat(e.target.value))} className="w-full mt-2 accent-lemas-gold" data-testid="sim-budget-slider" />
                  <div className="flex justify-between text-xs text-[rgb(var(--lm-text-muted))] mt-1"><span>0€</span><span>6 000€</span><span>12 000€</span></div>
                </div>
              )}
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))] mb-1 block">
                  Taux cotisations {simTab === 'tns' ? 'TNS' : 'patronales'}
                </label>
                <div className="flex items-center gap-3">
                  <Input type="number" value={simTaux} step={0.5} onChange={(e) => setSimTaux(parseFloat(e.target.value) || 0)} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono w-24" data-testid="sim-taux-input" />
                  <span className="text-[rgb(var(--lm-text-secondary))] text-sm">%</span>
                </div>
              </div>
            </div>

            {/* Result side */}
            <div className="space-y-3">
              {(() => {
                let net, cotisations, total, annuelNet, annuelCot, annuelTotal;
                if (simMode === 'net') {
                  net = simNet;
                  cotisations = net * (simTaux / 100);
                  total = net + cotisations;
                } else {
                  total = simBudget;
                  net = total / (1 + simTaux / 100);
                  cotisations = total - net;
                }
                annuelNet = net * 12;
                annuelCot = cotisations * 12;
                annuelTotal = total * 12;

                const refPerson = simTab === 'tns' ? dirigeant : null;
                const refNet = refPerson?.salaire_net || 0;
                const refCot = refPerson ? calcCharges(refPerson) : 0;
                const diff = net - refNet;
                const diffCot = cotisations - refCot;
                const diffTotal = diff + diffCot;
                const cotLabel = simTab === 'tns' ? 'Cotisations TNS' : 'Cotisations patronales';
                const accentColor = simTab === 'tns' ? 'text-[rgb(var(--lm-gold))]' : 'text-blue-400';
                const borderColor = simTab === 'tns' ? 'border-lemas-gold/20' : 'border-blue-400/20';

                return (
                  <>
                    {simMode === 'budget' && (
                      <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 flex justify-between items-center border border-emerald-400/20">
                        <div>
                          <p className="text-xs text-emerald-400 font-semibold">
                            {simTab === 'tns' ? 'Rémunération nette possible' : 'Salaire net possible'}
                          </p>
                          <p className="text-xs text-[rgb(var(--lm-text-muted))]">pour un budget de {formatCurrency(total)}</p>
                        </div>
                        <p className="font-mono text-xl text-emerald-400 font-bold">{formatCurrency(Math.round(net))}</p>
                      </div>
                    )}
                    <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{cotLabel}</p>
                        <p className="text-xs text-[rgb(var(--lm-text-muted))]">mensuel</p>
                      </div>
                      <p className="font-mono text-lg text-orange-400">{formatCurrency(Math.round(cotisations))}</p>
                    </div>
                    {simMode === 'net' && (
                      <div className={`bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 flex justify-between items-center border ${borderColor}`}>
                        <div>
                          <p className={`text-xs ${accentColor} font-semibold`}>Coût total mensuel</p>
                          <p className="text-xs text-[rgb(var(--lm-text-muted))]">net + cotisations</p>
                        </div>
                        <p className={`font-mono text-xl ${accentColor} font-bold`}>{formatCurrency(Math.round(total))}</p>
                      </div>
                    )}
                    <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Coût annuel</p>
                        <p className="text-xs text-[rgb(var(--lm-text-muted))]">net {formatCurrency(Math.round(annuelNet))} + cot. {formatCurrency(Math.round(annuelCot))}</p>
                      </div>
                      <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{formatCurrency(Math.round(annuelTotal))}</p>
                    </div>
                    {refPerson && diff !== 0 && (
                      <div className={`rounded-lg p-3 flex items-center gap-3 ${diff > 0 ? 'bg-orange-400/10' : 'bg-emerald-400/10'}`}>
                        <TrendingUp size={16} className={diff > 0 ? 'text-orange-400' : 'text-emerald-400'} />
                        <div className="flex-1">
                          <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                            {diff > 0 ? 'Surcoût' : 'Économie'} vs actuel ({formatCurrency(refNet)})
                          </p>
                          <p className={`font-mono text-sm font-semibold ${diff > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                            {diff > 0 ? '+' : ''}{formatCurrency(Math.round(diffTotal))}/mois ({diff > 0 ? '+' : ''}{formatCurrency(Math.round(diffTotal * 12))}/an)
                          </p>
                          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-0.5">
                            dont {diff > 0 ? '+' : ''}{formatCurrency(Math.round(diff))} net et {diffCot > 0 ? '+' : ''}{formatCurrency(Math.round(diffCot))} cotisations
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {editingSalary && (
        <Dialog open={!!editingSalary} onOpenChange={() => setEditingSalary(null)}>
          <DialogContent className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))]">
            <DialogHeader>
              <DialogTitle className="font-title text-xl">Modifier — {editingSalary.nom}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Nom</label>
                <Input value={editingSalary.nom} onChange={(e) => setEditingSalary({...editingSalary, nom: e.target.value})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="edit-name" />
              </div>
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Salaire net (ce que reçoit le salarié)</label>
                <Input type="number" value={editingSalary.salaire_net} onChange={(e) => setEditingSalary({...editingSalary, salaire_net: parseFloat(e.target.value) || 0})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" data-testid="edit-net" />
              </div>
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Salaire brut</label>
                <Input type="number" value={editingSalary.salaire_brut || ''} onChange={(e) => setEditingSalary({...editingSalary, salaire_brut: parseFloat(e.target.value) || 0})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" placeholder="0.00" data-testid="edit-brut" />
              </div>
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Type</label>
                <select value={editingSalary.type_contrat || 'salarie'} onChange={(e) => {
                  const type = e.target.value;
                  setEditingSalary({...editingSalary, type_contrat: type, taux_charges: type === 'dirigeant_tns' ? 45 : editingSalary.taux_charges });
                }} className="w-full bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] rounded-md px-3 py-2 text-sm" data-testid="edit-type">
                  <option value="salarie">Salarié</option>
                  <option value="dirigeant_tns">Dirigeant / Travailleur Indépendant</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-[rgb(var(--lm-text-secondary))]">Taux cotisations patronales (%)</label>
                <Input type="number" value={editingSalary.taux_charges} onChange={(e) => setEditingSalary({...editingSalary, taux_charges: parseFloat(e.target.value) || 0})} className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" data-testid="edit-taux" />
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">Appliqué sur le brut pour calculer les cotisations</p>
              </div>
              <div className="bg-[rgb(var(--lm-surface-elevated))] rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[rgb(var(--lm-text-secondary))]">Cotisations</span>
                  <span className="font-mono text-orange-400">{formatCurrency(calcCharges(editingSalary))}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-[rgb(var(--lm-text-secondary))]">Coût total</span>
                  <span className="font-mono text-[rgb(var(--lm-gold))] font-semibold">{formatCurrency(calcTotal(editingSalary))}</span>
                </div>
              </div>
              <Button onClick={() => handleUpdate(editingSalary)} className="w-full bg-lemas-gold text-lemas-bg" data-testid="confirm-edit-btn">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PatronSalaires;
